import { twMerge } from 'tailwind-merge';

import { getRelationshipDefinition } from '@/sections/insights/queryBuilder/entities/relationships/utils';
import { useQueryData } from '@/sections/insights/queryBuilder/state';
import type { FlatGraphEdge } from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { QueryFilter } from '@/types';

import { EdgeLabel } from './EdgeCard/EdgeLabel';
import { NodeFilterGroup } from './NodeCard/NodeFilterGroup';
import { normalizeFilters } from './utils/normalizeFilter';

interface EdgeCardProps {
  edge: FlatGraphEdge;
  isExpanded: boolean;
  onToggle: (edgeId: string) => void;
  editable?: boolean;
  onUpdateEdgeData?: (
    edgeId: string,
    data: Partial<FlatGraphEdge['data']>
  ) => void;
  onUpdateEdgeFilters?: (edgeId: string, filters: QueryFilter[]) => void;
}

/**
 * EdgeCard - Collapsible card displaying a query relationship
 */
export const EdgeCard = ({
  edge,
  isExpanded,
  onToggle,
  editable = false,
  onUpdateEdgeData,
  onUpdateEdgeFilters,
}: EdgeCardProps) => {
  // Get flatGraph from context to lookup source/target nodes
  const { queryState } = useQueryData();

  // Guard against null queryState
  if (!queryState) {
    return null;
  }

  const { flatGraph } = queryState;

  // Look up source and target nodes
  const sourceNode = flatGraph.nodes.get(edge.sourceId);
  const targetNode = flatGraph.nodes.get(edge.targetId);

  // Guard against missing nodes
  if (!sourceNode || !targetNode) {
    console.error('EdgeCard: Missing source or target node', {
      edgeId: edge.id,
      sourceId: edge.sourceId,
      targetId: edge.targetId,
    });
    return null;
  }

  // Look up relationship definition to check for filter support
  const firstLabel = Array.isArray(edge.data.label)
    ? edge.data.label[0]
    : edge.data.label;
  const relationshipDef = firstLabel
    ? getRelationshipDefinition(firstLabel)
    : undefined;
  const filterDefinitions = relationshipDef?.getSimpleFilters?.() ?? null;
  const filters = normalizeFilters(edge.data.filters);
  const filterCount = (edge.data.filters ?? []).length;

  // Wrapper to adapt NodeFilterGroup's (id, filters) callback to edge filters
  const handleUpdateFilters = onUpdateEdgeFilters
    ? (_id: string, newFilters: QueryFilter[]) => {
        onUpdateEdgeFilters(edge.id, newFilters);
      }
    : undefined;

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
      <EdgeLabel
        edge={edge}
        sourceNode={sourceNode}
        targetNode={targetNode}
        isExpanded={isExpanded}
        editable={editable}
        filterCount={filterCount}
        hasFilters={filterDefinitions !== null}
        onToggle={onToggle}
        onUpdateEdgeData={onUpdateEdgeData}
      />

      {/* Expandable content with CSS Grid collapse — only when relationship supports filters */}
      {filterDefinitions && (
        <div
          id={`edge-content-${edge.id}`}
          role="region"
          aria-label={`${firstLabel} relationship details`}
          className={twMerge(
            'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
            isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="overflow-hidden">
            <NodeFilterGroup
              node={{ id: edge.id }}
              filters={filters}
              editable={editable}
              isLoadingFields={false}
              filterDefinitions={filterDefinitions}
              firstLabel={firstLabel ?? ''}
              onUpdateFilters={handleUpdateFilters}
            />
          </div>
        </div>
      )}
    </div>
  );
};
